import React, {useState, useEffect, useRef} from 'react'
import {Box, Button, Image, Flex} from '@chakra-ui/react'
import useFluidConfigureDetails from '../../hooks/use-fluid-configure-details'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useSearchParams} from '../../hooks/use-search-params'
import {CategoryDetails} from './partials/category-details'
import {getUrl} from '../../utils/cloudinary-utils'
import {MAX_CACHE_AGE, STALE_WHILE_REVALIDATE} from '../../constants'
import Seo from '../../components/seo'
import OptiversalLinks from '../product-detail/partials/optiversal-links'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import PageFooter from '../product-list/partials/page-footer'
import {
    useAccessToken,
    useCommerceApi,
    useCustomerType,
    useCustomerOrders,
    useCustomerId
} from '@salesforce/commerce-sdk-react'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {getTotalQuantity} from '../../utils/basket-utils'
import {getColorBarItemGroupId} from '../../utils/product-utils'
import useTagManager from '../../hooks/use-tag-manager'
import usePaginatedProducts from '../../hooks/use-paginated-products'
import {useGlobaleContext} from '../../contexts/globale'
import useSiteCategory from '../../hooks/use-site-category'
import {useLocation} from 'react-router-dom'
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui'
import useMonetate from '../../hooks/use-monetate'
import {useWishList} from '../../hooks/use-wish-list'

const ColorBarPLP = () => {
    const navigate = useNavigation()
    const isMobile = useBreakpointValue({base: true, md: false})
    const [searchParams] = useSearchParams()
    const {getTokenWhenReady} = useAccessToken()
    const api = useCommerceApi()
    const {data: fluidConfigure} = useFluidConfigureDetails()
    let title = 'Customize Your Own Jewelry, Bridal, Home Décor | Kendra Scott'
    let description =
        'Design your own jewelry, customize bracelets, build designer rings at the Kendra Scott Color Bar. Use our Online Tool for wedding earrings and home décor gifts.'
    const {category} = searchParams || {}

    let colorBarURL = ''
    let plpCBConfig = []
    const [selectedCategory, setSelectedCategory] = useState(null)
    const {isRegistered} = useCustomerType()
    const {countryISO, currencyCode} = useGlobaleContext()
    const plpRef = useRef()
    const monetate = useMonetate()
    const customerClientId = useCustomerId()

    const {
        app: {commerceAPI}
    } = getConfig()
    const {configOptiversal} = commerceAPI.optiversal || {}
    var enabledRelatedLinksPLP = false
    var maxLinksPLP = 10
    const {changeUp} = commerceAPI
    const {configChangeup} = changeUp || {}
    var changeUpProductID = null

    try {
        colorBarURL = fluidConfigure.colorBarURL
        description = fluidConfigure.colorBarPlpDescription
        title = fluidConfigure.colorBarPlpTitle
        plpCBConfig = fluidConfigure.plpCBConfig
        const jsonConfig = JSON.parse(configOptiversal)
        enabledRelatedLinksPLP = jsonConfig.enabledRelatedLinksPLP
        maxLinksPLP = jsonConfig.maxLinksPLP
        window.CBContent = plpCBConfig
        const jsonChangeUpConfig = JSON.parse(configChangeup)
        changeUpProductID = jsonChangeUpConfig.enabled ? jsonChangeUpConfig.productID : null
    } catch (e) {
        console.log(e.message)
    }

    const activeCategory = selectedCategory
        ? selectedCategory
        : category
        ? plpCBConfig?.find((c) => c.category.toLowerCase() === category.toLowerCase())
        : plpCBConfig?.find((c) => c.category.toLowerCase().indexOf('all') > -1)

    const {data: apiCategory} = useSiteCategory(
        {
            parameters: {
                id: activeCategory?.id,
                levels: 1
            }
        },
        {enabled: !!activeCategory}
    )

    const productIDs = activeCategory?.styles?.map((style) => style.SKUforStylePrice)

    const {products, fetchMore, hasMore, isFetchingMore} = usePaginatedProducts({
        productIdArray: productIDs,
        activeCategory,
        api,
        getTokenWhenReady
    })

    const tagManager = useTagManager()
    const debounceRef = useRef(null)
    const {data: customer} = useCurrentCustomer()
    const {data: basket, isLoading: isBasketLoading} = useCurrentBasket()
    const cookie =
        onClient() &&
        new CookieStorage({
            keySuffix: '',
            sharedContext: !onClient()
        })

    const totalQuantity = getTotalQuantity(basket, changeUpProductID)
    const [globalDataLoaded, setGlobalDataLayerLoaded] = useState(false)
    const [dataLayerLoaded, setDataLayerLoaded] = useState(false)
    const pageCategory = 'colorbar'

    useEffect(() => {
        const city = cookie.get('city')
        const country = cookie.get('countryCode') || 'US'
        const state = cookie.get('state')
        if (!country) return

        if (
            ((customer?.customerType === 'registered' && customer?.email) ||
                customer?.customerType === 'guest') &&
            !isBasketLoading &&
            !globalDataLoaded
        ) {
            if (debounceRef.current) clearTimeout(debounceRef.current)

            debounceRef.current = setTimeout(() => {
                tagManager.globalDataLoad(
                    'plp',
                    pageCategory,
                    totalQuantity,
                    basket?.productTotal,
                    customer,
                    city,
                    country,
                    state
                )
                setGlobalDataLayerLoaded(true)
            }, 2000)
        }
    }, [customer?.customerType, customer?.email, isBasketLoading, cookie])

    const location = useLocation()

    useEffect(() => {
        if (!!customerClientId && customer?.customerType === 'registered' && isOrdersLoading) {
            return
        }
        // 'CookieStorage' takes some miliseconds to fetch cookies, that's why we've used 'useEffect'
        const city = cookie.get('city')
        const country = cookie.get('countryCode') || 'US'
        const state = cookie.get('state')
        if (
            globalDataLoaded &&
            !dataLayerLoaded &&
            ((customer?.customerType === 'registered' && customer?.email) ||
                customer?.customerType === 'guest')
        ) {
            const breadCrumb = `${pageCategory} / ${activeCategory?.category}`
            try {
                tagManager.dataLayerLoad(
                    'PLP',
                    isRegistered,
                    breadCrumb,
                    countryISO,
                    currencyCode,
                    customer,
                    city,
                    country,
                    state,
                    null,
                    null,
                    pageCategory,
                    orders?.length
                )
            } catch (e) {
                tagManager.dataLayerLoad(
                    'PLP',
                    isRegistered,
                    breadCrumb,
                    null,
                    null,
                    customer,
                    city,
                    country,
                    state,
                    null,
                    null,
                    pageCategory,
                    orders?.length
                )
            }
            const impressionBatchSize = 4
            const viewListBatchSize = isMobile ? 4 : 6
            const apiHits = activeCategory?.styles?.map((hit, i) => ({...hit, index: i}))
            window.__dataLayerLoaded = true
            for (let i = 0; i < apiHits?.length; i += impressionBatchSize) {
                const batch = apiHits?.slice(i, i + impressionBatchSize)
                tagManager.productImpression(
                    batch,
                    breadCrumb,
                    activeCategory.category,
                    pageCategory
                )
            }
            for (let i = 0; i < apiHits?.length; i += viewListBatchSize) {
                const batch = apiHits.slice(i, i + viewListBatchSize)
                const enrichedViewBatch = batch.map((item) => {
                    const productData = products?.find((p) => p.id === item.SKUforStylePrice)
                    if (!productData || !item) return
                    return {
                        ...productData
                    }
                })

                const params = new URLSearchParams(location.search)
                const categoryFromUrl = params.get('category')
                const formattedCategory = categoryFromUrl ? categoryFromUrl.toLowerCase() : ''

                tagManager.viewItemLists(
                    enrichedViewBatch,
                    undefined,
                    {
                        method: 'colorbar plp',
                        listId: `colorbar plp${formattedCategory ? ` - ${formattedCategory}` : ''}`,
                        listName: `colorbar plp${
                            formattedCategory ? ` - ${formattedCategory}` : ''
                        }`
                    },
                    true
                )
            }
            window.__dataLayerLoaded = false
            setDataLayerLoaded(true)
        }
    }, [
        globalDataLoaded,
        dataLayerLoaded,
        customer?.customerType,
        customer?.email,
        activeCategory?.category
    ])

    const prevProductsLengthRef = useRef(0)
    const isFirstLoadRef = useRef(true)

    useEffect(() => {
        // skip initial load (already tracked)
        if (isFirstLoadRef.current) {
            if (products?.length) {
                prevProductsLengthRef.current = products.length
                isFirstLoadRef.current = false
            }
            return
        }

        // wait until fetchMore is done
        if (isFetchingMore) return

        const prevLength = prevProductsLengthRef.current
        const currentLength = products?.length || 0

        if (currentLength <= prevLength) return

        const newItems = products.slice(prevLength).filter(Boolean)

        if (!newItems.length) return

        const viewListBatchSize = isMobile ? 4 : 6

        const params = new URLSearchParams(location.search)
        const categoryFromUrl = params.get('category')
        const formattedCategory = categoryFromUrl ? categoryFromUrl.toLowerCase() : ''

        window.__dataLayerLoaded = true
        for (let i = 0; i < newItems.length; i += viewListBatchSize) {
            const batch = newItems.slice(i, i + viewListBatchSize)

            tagManager.viewItemLists(
                batch,
                undefined,
                {
                    method: 'colorbar plp',
                    listId: `colorbar plp${formattedCategory ? ` - ${formattedCategory}` : ''}`,
                    listName: `colorbar plp${formattedCategory ? ` - ${formattedCategory}` : ''}`
                },
                true
            )
        }
        window.__dataLayerLoaded = false

        // update ref AFTER tracking
        prevProductsLengthRef.current = currentLength
    }, [isFetchingMore])

    useEffect(() => {
        if (products?.length && prevProductsLengthRef.current === 0) {
            prevProductsLengthRef.current = products.length
        }
    }, [])

    useEffect(() => {
        setGlobalDataLayerLoaded(false)
        setDataLayerLoaded(false)
    }, [activeCategory?.category])

    const {data: wishlist, isLoading: isWishlistLoading} = useWishList()
    const orderSearchParams = useSearchParams({limit: 100, offset: 0})
    const {limit, offset} = orderSearchParams[0]
    const {data: {data: orders} = {}, isLoading: isOrdersLoading} = useCustomerOrders(
        {
            parameters: {customerId: customerClientId, limit, offset}
        },
        {enabled: onClient() && !!customerClientId && customer?.customerType === 'registered'}
    )

    useEffect(() => {
        if (!activeCategory?.category || !products?.length) return
        triggerMonetateEvent()
    }, [products, selectedCategory, customer?.customerType, basket, wishlist, orders])

    const triggerMonetateEvent = () => {
        let PLPProducts = []

        const visibleProducts = (activeCategory?.styles ?? []).filter((style) =>
            products.find((product) => style.SKUforStylePrice === product.id)
        )

        visibleProducts.map((style) => {
            const productData = products.find((product) => product.id === style.SKUforStylePrice)
            PLPProducts.push({
                productId: getColorBarItemGroupId(productData?.name || style.name),
                id: style.productId
            })
        })

        const idString = 'index'
        const basketLength = plpRef.current?.getAttribute('data-basket-items-length')
            ? JSON.parse(plpRef.current?.getAttribute('data-basket-items-length'))
            : []
        const isCustomerRegistered = plpRef.current?.getAttribute('data-is-registered') === 'true'
        const wishlistLength = plpRef.current?.getAttribute('data-wishlist-length') || 0
        const basketGiftLength = plpRef.current?.getAttribute('data-basket-gift-length')
            ? JSON.parse(plpRef.current?.getAttribute('data-basket-gift-length'))
            : []

        const breadcrumb = ['Home', 'Color Bar', activeCategory?.category].filter(Boolean)

        if (breadcrumb) {
            window.mtBreadcrumb = breadcrumb
            monetate.monetateTrackPage(
                idString,
                breadcrumb,
                basketLength,
                isCustomerRegistered,
                wishlistLength,
                PLPProducts,
                null,
                null,
                basketGiftLength
            )
            window.monetateRef = activeCategory?.category
        }
    }

    useEffect(() => {
        if (
            (customer?.customerType === 'registered' && customer?.email) ||
            customer?.customerType === 'guest'
        ) {
            plpRef.current?.setAttribute(
                'data-is-registered',
                customer?.customerType === 'registered'
            )
            window.birthdayMonth = customer?.c_birthdayMonth
            window.monetatePhilantropyShopper = customer?.c_monetatePhilantropyShopper
        }
    }, [customer?.customerType, customer?.email, plpRef])

    useEffect(() => {
        if (!isOrdersLoading && plpRef) {
            window.lastOrderDate = orders?.[0]?.creationDate || 'n/a'
            plpRef.current?.setAttribute('data-last-order-date', orders?.[0]?.creationDate || 'n/a')
        }
    }, [isOrdersLoading, plpRef])

    useEffect(() => {
        if (!isWishlistLoading && plpRef) {
            plpRef.current?.setAttribute(
                'data-wishlist-length',
                wishlist?.customerProductListItems?.length || 0
            )
        }
    }, [isWishlistLoading, plpRef])

    useEffect(() => {
        if (!isBasketLoading && plpRef) {
            plpRef.current?.setAttribute(
                'data-basket-items-length',
                basket?.productItems?.length ? JSON.stringify(basket?.productItems) : '[]'
            )
            plpRef.current?.setAttribute(
                'data-basket-gift-length',
                basket?.giftCertificateItems?.length
                    ? JSON.stringify(basket?.giftCertificateItems)
                    : '[]'
            )
        }
    }, [isBasketLoading, plpRef, basket])

    useEffect(() => {
        var intervalId = setInterval(() => {
            const el = plpRef.current

            const isReady =
                (el?.getAttribute('data-last-order-date') ||
                    customer?.customerType !== 'registered') &&
                (el?.getAttribute('data-wishlist-length') ||
                    customer?.customerType !== 'registered') &&
                el?.getAttribute('data-basket-items-length') !== '' &&
                el?.getAttribute('data-is-registered')

            if (isReady) {
                triggerMonetateEvent()
                clearInterval(intervalId) // stop once done
            }
        }, 1000)

        return () => {
            clearInterval(intervalId)
        }
    }, [])

    const {res} = useServerContext()

    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    return (
        <Box
            ref={plpRef}
            data-testid="color-bar-plp"
            data-wishlist-length=""
            data-last-order-date=""
            data-basket-items-length=""
            data-basket-gift-length=""
            data-is-registered=""
        >
            <Seo title={title} description={description} />

            {/* Hero logo */}
            <Image
                src={getUrl('UIImages/images/color-bar-hero-logo-md.svg')}
                width="100%"
                maxWidth="310px"
                height="auto"
                alt="Color Bar Logo"
                margin="40px auto"
            />

            <Box ml={4} mb={4}>
                <Flex gap={2} overflowX="auto">
                    {plpCBConfig.map((c, idx) => (
                        <Button
                            key={idx}
                            variant={activeCategory?.category === c.category ? 'solid' : 'outline'}
                            borderRadius="0 !important"
                            borderColor="black.300"
                            padding="8px 30px"
                            lineHeight="normal"
                            textTransform="uppercase"
                            fontSize="13px"
                            height="unset"
                            onClick={() => {
                                setSelectedCategory(c)
                                navigate(`?category=${c.category}`)
                                monetate.setCategoriesViewed(c.category)
                                monetate.setNavigationClicks(c.category)
                            }}
                            flexShrink="0"
                        >
                            {c.category}
                        </Button>
                    ))}
                </Flex>
            </Box>

            {/* Active category selected */}
            {activeCategory && (
                <Box>
                    {/* Category details */}
                    <CategoryDetails
                        styles={activeCategory.styles}
                        colorBarURL={colorBarURL}
                        products={products}
                        category={activeCategory.category || 'colorbar'}
                        fetchMore={fetchMore}
                        isFetchingMore={isFetchingMore}
                        hasMore={hasMore}
                    />
                </Box>
            )}

            {apiCategory?.c_bottomCategoryHeading && apiCategory?.c_bottomCategoryDescription && (
                <PageFooter category={apiCategory} />
            )}

            {enabledRelatedLinksPLP && activeCategory && (
                <OptiversalLinks
                    query={activeCategory?.category}
                    isplp={true}
                    maxLinks={maxLinksPLP}
                    queryType="topic"
                />
            )}
        </Box>
    )
}

ColorBarPLP.getTemplateName = () => 'ColorBarPLP'

export default ColorBarPLP
